'use client';

import { useRef, useMemo, useCallback } from 'react';
import { PanZoom } from 'react-easy-panzoom';
import Cell from '@modules/garden-planner/views/garden_grid_cell_web';

interface AvailableCell {
  row: number;
  col: number;
}

interface PlantedPlant {
  row: number;
  col: number;
  slot: number;
  plantId: string;
}

interface PlantMeta {
  id: string;
  name: string;
  emoji: string;
  imagePath?: string;
  speciesName?: string;
}

interface GardenGridViewProps {
  rows: number;
  columns: number;
  availableCells: AvailableCell[];
  plantedPlants: PlantedPlant[];
  plants: PlantMeta[];
  selectedPlant: string | null;
  isAvailabilityMode: boolean;
  isCellSettingsMode: boolean;
  isClearMode: boolean;
  getSlotsForCell: (row: number, col: number) => number;
  handleCellClick: (row: number, col: number, event: React.MouseEvent) => void;
  isCellAvailable: (availableCells: AvailableCell[], row: number, col: number) => boolean;
  isBulkEditMode: boolean;
  bulkSelectedKeys: ReadonlySet<string>;
}

const EMPTY_PLANTS: PlantedPlant[] = [];

const GRID_STYLE = {
  display: 'grid',
  gap: 8,
  width: 'fit-content',
} as const;

export function GardenGridView({
  rows,
  columns,
  availableCells,
  plantedPlants,
  plants,
  selectedPlant,
  isAvailabilityMode,
  isCellSettingsMode,
  isClearMode,
  getSlotsForCell,
  handleCellClick,
  isCellAvailable,
  isBulkEditMode,
  bulkSelectedKeys,
}: GardenGridViewProps) {
  const panZoomRef = useRef<any>(null);
  const cellPlantsMapRef = useRef<Map<string, PlantedPlant[]>>(new Map());

  const plantLookup = useMemo(() => {
    const m = new Map<string, PlantMeta>();
    for (const p of plants) {
      m.set(p.id, p);
    }
    return m;
  }, [plants]);

  const cellPlantsMap = useMemo(() => {
    const prevMap = cellPlantsMapRef.current;
    const next = new Map<string, PlantedPlant[]>();

    for (const p of plantedPlants) {
      const key = `${p.row}-${p.col}`;
      const arr = next.get(key);
      if (arr) {
        arr.push(p);
      } else {
        next.set(key, [p]);
      }
    }

    for (const [key, arr] of next) {
      const prevArr = prevMap.get(key);
      if (
        prevArr &&
        prevArr.length === arr.length &&
        prevArr.every(
          (plant, index) =>
            plant.row === arr[index].row &&
            plant.col === arr[index].col &&
            plant.slot === arr[index].slot &&
            plant.plantId === arr[index].plantId
        )
      ) {
        next.set(key, prevArr);
      }
    }

    cellPlantsMapRef.current = next;
    return next;
  }, [plantedPlants]);

  const getPlantById = useCallback((id: string) => plantLookup.get(id), [plantLookup]);

  const getPlantsForCell = useCallback(
    (row: number, col: number) => cellPlantsMap.get(`${row}-${col}`) ?? EMPTY_PLANTS,
    [cellPlantsMap]
  );

  const cellCoords = useMemo(() => {
    const coords: Array<{ row: number; col: number }> = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < columns; col++) {
        coords.push({ row, col });
      }
    }
    return coords;
  }, [rows, columns]);

  const gridStyle = useMemo(
    () => ({
      ...GRID_STYLE,
      gridTemplateColumns: `repeat(${columns}, 80px)`,
    }),
    [columns]
  );

  const gridClassName = useMemo(() => {
    const classes = ['garden-grid'];
    if (selectedPlant && !isAvailabilityMode && !isCellSettingsMode && !isClearMode) {
      classes.push('garden-grid--has-brush');
    }
    return classes.join(' ');
  }, [selectedPlant, isAvailabilityMode, isCellSettingsMode, isClearMode]);

  return (
    <div style={{ height: '100%', minHeight: 0, flex: 1 }}>
      <div className="garden-grid-panel">
        <PanZoom ref={panZoomRef} disableDoubleClickZoom={true}>
          <div className={gridClassName} style={gridStyle}>
            {cellCoords.map(({ row, col }) => (
              <Cell
                key={`${row}-${col}`}
                row={row}
                col={col}
                plantsForCell={getPlantsForCell(row, col)}
                getPlantById={getPlantById}
                slots={getSlotsForCell(row, col)}
                isAvailable={isCellAvailable(availableCells, row, col)}
                isAvailabilityMode={isAvailabilityMode}
                isCellSettingsMode={isCellSettingsMode}
                isClearMode={isClearMode}
                isBulkSelected={isBulkEditMode && bulkSelectedKeys.has(`${row}-${col}`)}
                onClick={handleCellClick}
              />
            ))}
          </div>
        </PanZoom>
      </div>
    </div>
  );
}
